import { Response as ApplicationResponse } from '../../../api/typegen/client/application/get';
import { Response as ApplicationStatusResponse } from '../../../api/typegen/client/application/check-status/get';
import { getFetchHeaders } from '../../../utils/fetchHeaders';
import { isNotProduction } from '../../../utils/isNotProduction';
import { LOGIN_ROUTE } from '../../../utils/constants';
import { useApplicationResolution } from '../../../hooks/utilities/useApplicationResolution';
import { useEffect, useRef, useState } from 'react';
import {
  useGetClientApplication,
  useGetClientApplicationCheckStatus,
} from '../../../api';
import { usePolling } from '../../../hooks/usePolling';
import useTink from '../../../hooks/useTink';

const POLLING_INTERVAL = 5000;
const MAX_ATTEMPTS = 3;

const LOGIN_ERROR_REDIRECTION_ROUTE = `${LOGIN_ROUTE}?errorMessage=validation.session`;

export default function useResolutionResolverOnlineBank() {
  const { fetch: fetchGetApplication } = useGetClientApplication();
  const { handleTinkV2Error } = useTink();
  const { fetch: fetchGetClientApplicationCheckStatus } =
    useGetClientApplicationCheckStatus();
  const { navigateByApplicationResolution } = useApplicationResolution();

  const attemptsApplication = useRef<number>(0);
  const attemptsStatus = useRef<number>(0);

  const [application, setApplication] = useState<
    ApplicationResponse | null | undefined
  >(null);
  const [applicationStatus, setApplicationStatus] =
    useState<ApplicationStatusResponse | null>(null);

  const fetchApplication = async () => {
    try {
      const applicationResponse = await fetchGetApplication({
        headers: getFetchHeaders({
          Accept: 'application/vnd.4finance.web.v1.hal+json',
          'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
        }),
      });
      if (applicationResponse) setApplication(applicationResponse);
      else throw Error();
    } catch {
      if (attemptsApplication.current > MAX_ATTEMPTS) {
        applicationPolling.stopPolling();
        window.location.replace(LOGIN_ERROR_REDIRECTION_ROUTE);
      } else {
        attemptsApplication.current += 1;
      }
    }
  };

  const fetchApplicationStatus = async () => {
    try {
      const response = await fetchGetClientApplicationCheckStatus();
      if (response) setApplicationStatus(response);
      else throw Error();
    } catch {
      if (attemptsStatus.current > MAX_ATTEMPTS) {
        clientApplicationCheckStatusPolling.stopPolling();
        window.location.replace(LOGIN_ERROR_REDIRECTION_ROUTE);
      } else {
        attemptsStatus.current += 1;
      }
    }
  };

  const applicationPolling = usePolling(fetchApplication);
  const clientApplicationCheckStatusPolling = usePolling(
    fetchApplicationStatus,
  );

  /**
   * Effects
   */
  useEffect(() => {
    clientApplicationCheckStatusPolling.startPolling(POLLING_INTERVAL);

    return () => clientApplicationCheckStatusPolling.stopPolling();
  }, []);

  useEffect(() => {
    (function handleCanPollApplication() {
      if (applicationStatus?.completed !== true || applicationPolling.isEnabled)
        return;

      clientApplicationCheckStatusPolling.stopPolling();
      setTimeout(
        () => {
          applicationPolling.startPolling(POLLING_INTERVAL);
        },
        // Note: Hack to avoid Tink Extension's mock race condition
        isNotProduction() ? 15000 : 0,
      );
    })();
  }, [
    clientApplicationCheckStatusPolling.pollingAttempts,
    applicationStatus?.completed,
    applicationPolling.isEnabled,
  ]);

  useEffect(() => {
    (function handleApplicationResolution() {
      if (!application || !application?.resolution) return;

      applicationPolling.stopPolling();
      navigateByApplicationResolution();
    })();
  }, [application]);

  useEffect(() => {
    handleTinkV2Error();
  }, []);
}
